import { ChangeEvent, ReactNode, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";

export default function AlertDialog({
  children,
  textTitle,
  textContent,
  open,
  setOpen,
  closeLabel = "Cancel",
  confirmLabel = "Confirm",
  isConfirmDisabled = false,
  handleConfirm,
  sx,
  maxWidth = "md",
  isInputConfirmation,
  valueToBeDeleted,
  hintAfterInput = false,
  closeBtnStyle,
  confirmBtnStyle,
}: {
  children?: ReactNode;
  textTitle: string;
  textContent: string | ReactNode;
  open: boolean;
  setOpen: any;
  closeLabel?: string;
  confirmLabel?: string;
  isConfirmDisabled?: boolean;
  handleConfirm: () => void;
  sx?: any;
  maxWidth?: "sm" | "md" | "lg";
  isInputConfirmation?: boolean;
  valueToBeDeleted?: string;
  /** Renders the "Please type <name> to confirm" hint after the isInputConfirmation TextField instead of before it. No effect without isInputConfirmation. */
  hintAfterInput?: boolean;
  closeBtnStyle?: any;
  confirmBtnStyle?: any;
}) {
  const [inputValue, setInputValue] = useState("");

  const handleClose = () => {
    setInputValue("");
    setOpen(false);
  };

  const confirm = () => {
    handleClose();
    handleConfirm();
  };

  const hint = valueToBeDeleted && (
    <DialogContentText id="alert-dialog-confirm-label" sx={{ mt: 2 }}>
      Please type <strong>{valueToBeDeleted}</strong> to confirm
    </DialogContentText>
  );

  return (
    <Dialog
      fullWidth
      maxWidth={maxWidth}
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      sx={sx}
    >
      <DialogTitle id="alert-dialog-title">{textTitle}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          {textContent}
        </DialogContentText>
        <div>{children}</div>
        {!hintAfterInput && hint}
        {isInputConfirmation && (
          <TextField
            id="alert-dialog-confirm-input"
            sx={{ mt: 2 }}
            value={inputValue}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setInputValue(e.target.value);
            }}
            variant="outlined"
            fullWidth
          />
        )}
        {hintAfterInput && hint}
      </DialogContent>
      <DialogActions>
        <Button
          id="close-alert-dialog-modal"
          onClick={handleClose}
          sx={closeBtnStyle}
        >
          {closeLabel}
        </Button>
        <Button
          autoFocus
          id="alert-modal-confirm-btn"
          color={isInputConfirmation ? "error" : "primary"}
          // Both gates, not one: a dialog can want a typed name AND a condition met, as the
          // delete-pod one does when only Force delete makes the command succeed.
          disabled={
            isConfirmDisabled ||
            (isInputConfirmation && inputValue !== valueToBeDeleted)
          }
          onClick={confirm}
          sx={confirmBtnStyle}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
